import axios from 'axios'
import axiosInstance from '../axios'

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('access-Token-Edify')
  return { headers: { Authorization: `Bearer ${authToken}` } }
}

const getAuthToken = () => localStorage.getItem('access-Token-Edify')

export type DraftProjectPayload = {
  name?: string
  description?: string
  zone?: string
  startDate?: string
  endDate?: string
  active?: boolean
}

export type DraftSubprojectPayload = {
  tempId?: string
  detectedLabel?: string
  selectedSkillId?: number
  description?: string
  status?: 'OPEN' | 'CLOSE'
  skillMatchKind?: string
  matchedSkillName?: string
  matchScore?: number
  name?: string
  link?: string
  startDate?: string
  endDate?: string
  datesInheritedFromProject?: boolean
  sourcePages?: number[]
  documentationSlots?: {
    budget?: number[]
    metricComputation?: number[]
    technicalSpecifications?: number[]
    planos?: number[]
  }
  documentationFileIds?: {
    budget?: string
    metricComputation?: string
    technicalSpecifications?: string
    planos?: string
  }
}

export type DraftPatchPayload = {
  project?: DraftProjectPayload
  subprojects?: DraftSubprojectPayload[]
  metadata?: Record<string, any>
}

export const createProjectAutofillDraft = async (payload: DraftPatchPayload) => {
  const response = await axiosInstance.post('/project-autofill/draft', payload, getAuthHeaders())
  return response.data
}

export const getProjectAutofillDraft = async (draftId: string) => {
  const response = await axiosInstance.get(`/project-autofill/draft/${draftId}`, getAuthHeaders())
  return response.data
}

export const patchProjectAutofillDraft = async (draftId: string, payload: DraftPatchPayload) => {
  const response = await axiosInstance.patch(`/project-autofill/draft/${draftId}`, payload, getAuthHeaders())
  return response.data
}

export type PublishProjectAutofillDraftResult = {
  message?: string
  proyectoId?: string
  subproyectoIds?: string[]
  draftId?: string
}

export function parsePublishProjectAutofillDraftResponse(res: unknown): PublishProjectAutofillDraftResult {
  const out: PublishProjectAutofillDraftResult = {}
  if (!res || typeof res !== 'object') return out
  const r = res as Record<string, unknown>
  if (typeof r.message === 'string') out.message = r.message
  const inner = r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const pid = inner.proyectoId
  if (pid != null && String(pid).trim() !== '') out.proyectoId = String(pid).trim()
  const sids = inner.subproyectoIds
  if (Array.isArray(sids)) {
    out.subproyectoIds = sids.map((x) => String(x)).filter((s) => s.length > 0)
  }
  const did = inner.draftId
  if (did != null && String(did).trim() !== '') out.draftId = String(did).trim()
  return out
}

export function formatPublishProjectAutofillDraftError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data
    if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>
      const msg = o.message ?? o.error ?? o.mensaje
      if (typeof msg === 'string' && msg.trim()) return msg.trim()
      const blocking = o.blockingIssues
      if (Array.isArray(blocking) && blocking.length > 0) {
        const b = blocking.map((x) => String(x || '').trim()).filter(Boolean).join('. ')
        if (b) return b
      }
      const missing = o.missingForProjectAssign
      if (Array.isArray(missing) && missing.length > 0) {
        const m = missing.map((x) => String(x || '').trim()).filter(Boolean).join(', ')
        if (m) return m
      }
      const subIss = o.subprojectIssues
      if (Array.isArray(subIss) && subIss.length > 0) {
        const s = subIss
          .map((x) => {
            if (x && typeof x === 'object') {
              const r = x as Record<string, unknown>
              const parts = [r.message, r.tempId, r.skillId].filter((p) => p != null && String(p).trim() !== '')
              return parts.map(String).join(' — ')
            }
            return String(x || '').trim()
          })
          .filter(Boolean)
          .join('. ')
        if (s) return s
      }
    }
    return err.message || 'No se pudo publicar'
  }
  return 'No se pudo publicar'
}

export const publishProjectAutofillDraft = async (draftId: string): Promise<unknown> => {
  const response = await axiosInstance.post(
    `/project-autofill/draft/${draftId}/publish`,
    null,
    getAuthHeaders()
  )
  return response.data
}

export const postProjectAutofillDraftFile = async (
  draftId: string,
  file: File,
  tag?: string
) => {
  const token = getAuthToken()
  const fd = new FormData()
  fd.append('file', file)
  if (tag != null && String(tag).trim() !== '') {
    fd.append('tag', String(tag).trim())
  }
  const response = await axiosInstance.post(`/project-autofill/draft/${draftId}/files`, fd, {
    headers: { Authorization: `Bearer ${token}` }
  })
  return response.data
}

const DOCUMENTATION_PIPELINE_POLL_MAX_MS = 600000
const DOCUMENTATION_PIPELINE_POLL_INTERVAL_MS = 2000

export type DocumentationPipelineStatus = {
  phase: string
  detailPhase?: string
  errorMessage?: string
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export function parseDocumentationPipelinePayload(raw: unknown): DocumentationPipelineStatus {
  if (!raw || typeof raw !== 'object') {
    return { phase: 'processing' }
  }
  const r = raw as Record<string, unknown>
  const inner =
    r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const phase = typeof inner.phase === 'string' && inner.phase.length > 0 ? inner.phase : 'processing'
  const detailPhaseRaw = inner.detailPhase
  const detailPhase =
    typeof detailPhaseRaw === 'string' && detailPhaseRaw.trim().length > 0
      ? detailPhaseRaw.trim()
      : undefined
  const errorMessage =
    typeof inner.errorMessage === 'string' && inner.errorMessage.trim().length > 0
      ? inner.errorMessage.trim()
      : undefined
  return { phase, detailPhase, errorMessage }
}

export function documentationPipelinePhaseForLoader(status: {
  phase: string
  detailPhase?: string
}): string {
  const d =
    typeof status.detailPhase === 'string' && status.detailPhase.trim().length > 0
      ? status.detailPhase.trim()
      : ''
  if (
    (status.phase === 'processing' || status.phase === 'awaiting_analysis') &&
    d.length > 0
  ) {
    return d
  }
  return status.phase
}

export async function getProjectAutofillDocumentationPipeline(
  draftId: string
): Promise<DocumentationPipelineStatus> {
  const response = await axiosInstance.get(
    `/project-autofill/draft/${draftId}/documentation-pipeline`,
    getAuthHeaders()
  )
  return parseDocumentationPipelinePayload(response.data)
}

function pipelineError(
  code: string,
  message: string,
  userMessage?: string
): Error & { code?: string; userMessage?: string } {
  const e = new Error(message) as Error & { code?: string; userMessage?: string }
  e.code = code
  e.userMessage = userMessage ?? message
  return e
}

export async function waitForProjectAutofillDocumentationPipeline(
  draftId: string,
  onPhase: (phase: string) => void,
  options?: { maxWaitMs?: number; intervalMs?: number; signal?: AbortSignal }
): Promise<void> {
  const maxWaitMs = options?.maxWaitMs ?? DOCUMENTATION_PIPELINE_POLL_MAX_MS
  const intervalMs = options?.intervalMs ?? DOCUMENTATION_PIPELINE_POLL_INTERVAL_MS
  const deadline = Date.now() + maxWaitMs
  const signal = options?.signal

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      const err = new Error('Aborted') as Error & { name?: string }
      err.name = 'AbortError'
      throw err
    }
    const status = await getProjectAutofillDocumentationPipeline(draftId)
    const { phase, errorMessage } = status
    onPhase(documentationPipelinePhaseForLoader(status))
    if (phase === 'ready') {
      return
    }
    if (phase === 'failed') {
      const msg =
        errorMessage && errorMessage.length > 0
          ? errorMessage
          : 'El análisis del documento falló.'
      throw pipelineError('DOCUMENTATION_PIPELINE_FAILED', msg, msg)
    }
    await delay(intervalMs)
  }
  throw pipelineError(
    'STREAM_TIMEOUT',
    'STREAM_TIMEOUT',
    'El análisis del documento tardó demasiado. Intentá de nuevo en unos minutos.'
  )
}

export type PostProjectAutofillDraftFromDocumentResult =
  | { mode: 'async'; draftId: string; body: unknown }
  | { mode: 'sync'; body: unknown }

export async function postProjectAutofillDraftFromDocument(
  file: File
): Promise<PostProjectAutofillDraftFromDocumentResult> {
  const fd = new FormData()
  fd.append('file', file)
  const response = await axiosInstance.post('/project-autofill/draft/from-document', fd, {
    ...getAuthHeaders()
  })
  if (response.status === 202) {
    const raw = response.data
    const parsed = parsePublishProjectAutofillDraftResponse(raw)
    let draftId = parsed.draftId ?? ''
    if (raw && typeof raw === 'object') {
      const d = raw as Record<string, unknown>
      const inner =
        d.data && typeof d.data === 'object' ? (d.data as Record<string, unknown>) : d
      const id = inner.draftId
      if (id != null && String(id).trim() !== '') {
        draftId = String(id).trim()
      }
    }
    return { mode: 'async', draftId, body: raw }
  }
  return { mode: 'sync', body: response.data }
}
